import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAngularContext } from 'AngularContext';
import { useCurrentUser } from 'FrontRoyalAngular';
import getEnrollmentSidebarTodoGroups from 'Lessons/angularModule/scripts/stream/getEnrollmentSidebarTodoStuff';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { getUserProgramState } from 'Users';
import { getSupportEmailAddress, getProgramConfigValue, type ProgramType } from 'Program';
import parseHtml from 'html-react-parser';
import { type EnrollmentTodoGroups, type TodoItem } from '../StudentDashboard.types';
import { EnrollmentTodoItem } from './EnrollmentTodoItem';
import { enrollmentTodoGroupVisible, enrollmentTodoVisible, hasCompleteItem } from './helpers';

const PAST_DUE = 'pastDue';

export function EnrollmentBox() {
    return <EnrollmentBoxPropsWrapper />;
}

function EnrollmentBoxPropsWrapper() {
    const $injector = useAngularContext();
    const currentUser = useCurrentUser();
    const enrollmentTodoGroups = getEnrollmentSidebarTodoGroups($injector) as EnrollmentTodoGroups;
    const { xsOrSm } = useIsMobileMixin();
    const [_todos, todosByDate] = enrollmentTodoGroups;

    if (!currentUser) return null;

    const programType = getUserProgramState(currentUser)?.programType ?? null;

    return <EnrollmentBoxPresentation programType={programType} xsOrSm={xsOrSm} todosByDate={todosByDate} />;
}

export function EnrollmentBoxPresentation({
    programType,
    xsOrSm,
    todosByDate,
}: {
    programType: ProgramType | null;
    xsOrSm: boolean;
    todosByDate: { [dueDate: string]: TodoItem[] };
}) {
    const { t } = useTranslation('back_royal');
    const [showCompletedTodos, setShowCompletedTodos] = useState(false);

    const completedItemsToggleButtonIsVisible = useMemo(
        () => hasCompleteItem(Object.values(todosByDate).flat()),
        [todosByDate],
    );

    return (
        <div className="bg-transparent px-5 py-[30px] md:bg-[linear-gradient(168deg,_#a52e3b_0%,_#600b14_96%)]">
            {!xsOrSm && (
                <>
                    <h2 className="mx-auto my-0 text-center before:hidden">
                        <span className="mb-[5px] block text-xs font-normal uppercase text-white">
                            {t('lessons.stream.student_dashboard_enrollment.complete_your')}
                        </span>
                        <span className="block text-[28px] font-semibold uppercase text-white">
                            {t('lessons.stream.student_dashboard_enrollment.enrollment')}
                        </span>
                        <span className="mx-auto mb-0 mt-[10px] flex h-[40px] w-[40px] items-center justify-center rounded-full bg-yellow text-lg font-normal text-red-dark">
                            <FontAwesomeIcon icon={faFileLines} />
                        </span>
                    </h2>
                    <hr className="mx-auto my-5 h-0 w-[52px] border-b border-solid border-b-[#ffffff80] bg-transparent" />
                </>
            )}
            {Object.keys(todosByDate).map((dueDate: string) => {
                const todoItems = todosByDate[dueDate].filter(item => enrollmentTodoVisible(item, showCompletedTodos));
                return (
                    <Fragment key={dueDate}>
                        {dueDate === PAST_DUE && todoItems.length > 0 && (
                            <span className="flex items-center justify-start text-md leading-6 text-white">
                                {t('lessons.stream.student_dashboard_enrollment.past_due')}
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mx-1" />
                            </span>
                        )}
                        {dueDate !== PAST_DUE && enrollmentTodoGroupVisible(todoItems, showCompletedTodos) && (
                            <span className="due-by text-md leading-[26px] text-white">
                                {t('lessons.stream.student_dashboard_enrollment.due_by')} {dueDate}
                            </span>
                        )}
                        {todoItems.map(item => (
                            <EnrollmentTodoItem key={item.name} item={item} showCompletedTodos={showCompletedTodos} />
                        ))}
                    </Fragment>
                );
            })}
            {completedItemsToggleButtonIsVisible && (
                <button
                    type="button"
                    data-testid="show-completed-toggle"
                    onClick={() => setShowCompletedTodos(prev => !prev)}
                    className="h-auto border-none bg-transparent p-0 ps-6 text-xs normal-case leading-7 text-[#ffffff80] shadow-none"
                >
                    {t(
                        `lessons.stream.student_dashboard_enrollment.${
                            showCompletedTodos ? 'hide_completed_steps' : 'show_completed_steps'
                        }`,
                    )}
                </button>
            )}
            {programType && getProgramConfigValue<string>(programType, 'enrollmentFaqLink') && (
                <p className="sub-sub-text m-0 mt-5 text-center">
                    <span className="block text-sm text-white">
                        {parseHtml(
                            t('lessons.stream.student_dashboard_enrollment.learn_enrollment_requirements', {
                                enrollmentFaqLink: getProgramConfigValue<string>(programType, 'enrollmentFaqLink'),
                            }),
                        )}
                    </span>
                    <span className="block text-sm text-white">
                        {parseHtml(
                            t('lessons.stream.student_dashboard_enrollment.have_questions', {
                                brandEmail: getSupportEmailAddress(programType, 'registrar'),
                            }),
                        )}
                    </span>
                </p>
            )}
        </div>
    );
}
